// Which model the scene shows, where its file is, its fixed cameras and the
// names of its parts, from the model index (web/plugins/model-index.ts) and the
// named views (data/views/<group>.toml).

import { MODELS, loadPoseTable } from "virtual:psim/model-index";
import type { IndexedModel } from "../../plugins/model-index";
import { NAMED_VIEWS, type NamedView } from "../state/views";
import type { Camera, CutPlane } from "../state/view-state";
import type { ScenePart } from "./cutaway";
import { tablePose, type PoseSource } from "./pose";
import { strings } from "./strings";

export type { IndexedModel };
export { MODELS };

/** The group drawn until the engine's own model exists. */
export const FIXTURE_GROUP = "fixture";

/** The model group for an engine: the group named after it, else the fixture. */
export function modelFor(engine: string, models: readonly IndexedModel[] = MODELS): IndexedModel | undefined {
  return models.find((m) => m.group === engine) ?? models.find((m) => m.group === FIXTURE_GROUP);
}

/** The glb's address: public/ is served at the app's base. */
export function modelUrl(model: IndexedModel): string {
  return `${import.meta.env.BASE_URL}${model.url}?v=${model.sha256.slice(0, 12)}`;
}

/** The pose source that moves the model's joints. */
export async function poseFor(model: IndexedModel): Promise<PoseSource> {
  return tablePose(await loadPoseTable(model.module));
}

export interface CameraView {
  id: string;
  title: string;
  camera: Camera;
  /** The cutaway the view sets, or undefined when it leaves the cutaway alone. */
  cutaway: CutPlane[] | undefined;
}

/** The fixed cameras of a model group: the views in data/views/<group>.toml that set a camera. */
export function cameraViews(group: string, views: readonly NamedView[] = NAMED_VIEWS): CameraView[] {
  const file = `data/views/${group}.toml`;
  const out: CameraView[] = [];
  for (const v of views) {
    const camera = v.state.camera;
    if (v.file !== file || !camera) continue;
    out.push({
      id: v.id,
      title: v.title.replace(/^[^:]*:\s*/, ""),
      camera: camera as Camera,
      cutaway: (v.state.cutaway ?? undefined) as CutPlane[] | undefined,
    });
  }
  return out;
}

/** A part's name for people: its parts-list name, and its cylinder when it has one. */
export function partLabel(model: IndexedModel, part: ScenePart): string {
  const name = model.parts.find((p) => p.id === part.id)?.name ?? part.id;
  const n = /_(\d+)$/.exec(part.object.name)?.[1];
  return n === undefined ? name : `${name}, ${strings.cylinder} ${n}`;
}
